import { useState } from "react";
import { useLocale } from "@/hooks/useLocale";
import { Flag, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { getSessionUser } from "@/lib/auth-session";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export type ReportContentType =
  | "world"
  | "adventure_message"
  | "entity"
  | "world_description"
  | "forum_thread"
  | "forum_post";

export type ReportReason =
  | "inappropriate"
  | "spam"
  | "harassment"
  | "copyright"
  | "other";

const REASONS: { value: ReportReason; label: string; description: string }[] = [
  {
    value: "inappropriate",
    label: "Inappropriate content",
    description: "Sexual, violent, or otherwise unsuitable content.",
  },
  {
    value: "harassment",
    label: "Harassment or hate",
    description: "Targets a person or group with hateful or threatening content.",
  },
  {
    value: "spam",
    label: "Spam or scam",
    description: "Unwanted promotional content, scams, or repeated junk.",
  },
  {
    value: "copyright",
    label: "Copyright violation",
    description: "Uses material that infringes someone's copyright.",
  },
  {
    value: "other",
    label: "Something else",
    description: "Use the details field to describe the issue.",
  },
];

const DETAILS_MAX = 500;

export interface ReportContentButtonProps {
  contentType: ReportContentType;
  contentId: string;
  worldId?: string | null;
  /** Optional className passed to the trigger button (e.g. for sizing inside menus). */
  className?: string;
  /** When true, the trigger is rendered as a small icon-only button (default). */
  iconOnly?: boolean;
  /** Custom trigger element. If supplied, replaces the default flag button.
   *  Set to `null` to skip rendering any trigger (use with controlled `open`). */
  trigger?: React.ReactNode | null;
  /** Optional label shown next to the icon when iconOnly is false. */
  label?: string;
  /** Controlled open state. When provided, the component does not manage its own state. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ReportContentButton({
  contentType,
  contentId,
  worldId,
  className,
  iconOnly = true,
  trigger,
  label = "Report",
  open: controlledOpen,
  onOpenChange,
}: ReportContentButtonProps) {
  const { t } = useLocale();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : internalOpen;
  const [reason, setReason] = useState<ReportReason>("inappropriate");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setReason("inappropriate");
    setDetails("");
  };

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
    if (!next) reset();
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const user = await getSessionUser();
      if (!user) {
        toast.error(t.rep_sign_in_required);
        return;
      }

      // Pre-flight duplicate check — gives a friendlier error than the unique
      // index would. The partial unique index on (reporter_id, content_type,
      // content_id) WHERE status IN ('pending','reviewed') is still the
      // ultimate guard against races.
      const { data: existing, error: existingError } = await supabase
        .from("content_reports" as any)
        .select("id, status")
        .eq("reporter_id", user.id)
        .eq("content_type", contentType)
        .eq("content_id", contentId)
        .in("status", ["pending", "reviewed"])
        .maybeSingle();

      // PGRST116 == no rows; ignore. Other errors (e.g. transient network) we
      // surface but still attempt the insert — the unique index is the source
      // of truth.
      if (existingError && existingError.code !== "PGRST116") {
        // Non-fatal — fall through to insert and let the DB decide.
      }

      if (existing) {
        toast.info(t.rep_already_reported);
        handleOpenChange(false);
        return;
      }

      const trimmedDetails = details.trim();
      const { error } = await supabase
        .from("content_reports" as any)
        .insert({
          reporter_id: user.id,
          content_type: contentType,
          content_id: contentId,
          world_id: worldId ?? null,
          reason,
          details: trimmedDetails ? trimmedDetails : null,
          status: "pending",
        });

      if (error) {
        // Postgres unique-violation code 23505 — friendlier message.
        if ((error as any).code === "23505") {
          toast.info(t.rep_already_reported);
          handleOpenChange(false);
          return;
        }
        throw error;
      }

      toast.success(t.rep_submitted);
      handleOpenChange(false);
    } catch (e: any) {
      toast.error(t.rep_submit_failed, {
        description: e?.message || t.rep_try_later,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // `trigger === null` => caller controls open state and provides no trigger.
  const triggerEl =
    trigger === null
      ? null
      : (trigger ??
        (iconOnly ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn(
              "h-6 w-6 text-muted-foreground/70 hover:text-destructive",
              className,
            )}
            title="Report this content"
            aria-label="Report this content"
          >
            <Flag className="h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn("gap-1.5 text-muted-foreground hover:text-destructive", className)}
          >
            <Flag className="h-3.5 w-3.5" />
            {label}
          </Button>
        )));

  const detailsLength = details.length;
  const detailsOver = detailsLength > DETAILS_MAX;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {triggerEl !== null && <DialogTrigger asChild>{triggerEl}</DialogTrigger>}
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-destructive" />
            Report content
          </DialogTitle>
          <DialogDescription>
            Tell us what's wrong with this content. Our team will review your
            report. False reports may affect your account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="report-reason">Reason</Label>
            <Select
              value={reason}
              onValueChange={(v) => setReason(v as ReportReason)}
            >
              <SelectTrigger id="report-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    <div className="flex flex-col">
                      <span>{r.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {REASONS.find((r) => r.value === reason)?.description}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="report-details">
                Details <span className="text-muted-foreground">(optional)</span>
              </Label>
              <span
                className={cn(
                  "text-[10px] tabular-nums",
                  detailsOver
                    ? "text-destructive"
                    : "text-muted-foreground",
                )}
              >
                {detailsLength}/{DETAILS_MAX}
              </span>
            </div>
            <Textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={t.mod_report_details}
              maxLength={DETAILS_MAX + 50}
              rows={4}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || detailsOver}
            className="gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReportContentButton;
